/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 10th 2022, 2:59:50 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useNavigate } from "react-router-dom";
// import { Settings } from "react-feather";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { FormConfiguration } from "lib/schemas";
import { OSActionButton } from "ui-widgets";
import { RegistrationFormSettingsPopup } from "form-builder/components";
import { FormInfoPopUpForm } from "form-builder/components/FormInfoPopUpForm";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { ConditionIcon, FormIcon, NumberingIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";

interface FormBuilderTopBarProps {
  onSelectFormFields: () => void;
  onSelectFormFieldDependencies: () => void;
  onSelectAdvancedSettings: () => void;
  onSelectNumberingSequence: () => void;
  onUpdateFormInfo?: (form: FormConfiguration) => void;
  shareSubmissionLinkComponent?: React.ReactNode;
}

export const FormBuilderTopBar = ({
  onSelectFormFields,
  onSelectFormFieldDependencies,
  onSelectAdvancedSettings,
  onSelectNumberingSequence,
  onUpdateFormInfo,
  shareSubmissionLinkComponent,
}: FormBuilderTopBarProps) => {
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);

  const navigate = useNavigate();
  const onBackButtonClick = () => navigate(-1);

  const showRegistrationFormSettings =
    currentFormConfiguration.form_type === "registration_form" && currentFormConfiguration.fields_count > 0;

  return (
    <>
      <FormInfoPopUpForm
        closeFormInfoPopUpFn={null}
        formInfoPopUpOpenState={null}
        onAPISaveFormInfoCallBackFn={onUpdateFormInfo ?? function () {}}
      >
        {({ handleOpen: openFormInfoPopUpFn }) => (
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <Stack direction="row" spacing={2}>
                <OSActionButton actionStyle="back" variant="outlined" size="small" onClick={onBackButtonClick}>
                  Back
                </OSActionButton>
                <OSActionButton actionStyle={"edit"} variant="contained" size="small" onClick={openFormInfoPopUpFn}>
                  Edit Form Details
                </OSActionButton>
                {shareSubmissionLinkComponent}
              </Stack>
            </Grid>
            <Grid item xs={3}>
              <Tooltip title={<Typography>Form Fields</Typography>}>
                <IconButton color="secondary" onClick={onSelectFormFields}>
                  <FormIcon fill={COLORSCHEME.secondary} width={20} height={20} />
                </IconButton>
              </Tooltip>
              {showRegistrationFormSettings && (
                <RegistrationFormSettingsPopup>
                  {/* Note: Modals do not work properly within the IconButton component. State does not change in a predictable manner. */}
                  {({ handleOpen: openRegistrationSettingsPopup }) => (
                    <Tooltip title={<Typography>Configure settings for this registration form</Typography>}>
                      <IconButton onClick={openRegistrationSettingsPopup}>
                        <AppRegistrationIcon color="secondary" />
                      </IconButton>
                    </Tooltip>
                  )}
                </RegistrationFormSettingsPopup>
              )}
              <Tooltip title={<Typography>Field Dependencies</Typography>}>
                <IconButton color="secondary" onClick={onSelectFormFieldDependencies}>
                  <ConditionIcon fill={COLORSCHEME.secondary} width={20} height={20} />
                </IconButton>
              </Tooltip>

              <Tooltip title={<Typography>Form Submission Identifier</Typography>}>
                <IconButton color="secondary" onClick={onSelectNumberingSequence}>
                  <NumberingIcon fill={COLORSCHEME.secondary} width={20} height={20} />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title={<Typography>Advanced Form Settings</Typography>}>
                <IconButton color="secondary" onClick={onSelectAdvancedSettings}>
                  <Settings color={COLORSCHEME.secondary} size={20} />
                </IconButton>
              </Tooltip> */}
            </Grid>
          </Grid>
        )}
      </FormInfoPopUpForm>
    </>
  );
};
